import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Store, HandCoins, Bike, X, ShoppingBag, Truck, Layers, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { Icon: Store, label: 'Your own ordering page' },
  { Icon: HandCoins, label: 'No commissions, no markups' },
  { Icon: Bike, label: 'Pickup or your own delivery area' },
];

const SERVICES = [
  { id: 'pickup', label: 'Pickup', Icon: ShoppingBag },
  { id: 'delivery', label: 'Delivery', Icon: Truck },
  { id: 'both', label: 'Both', Icon: Layers },
];

const inputCls =
  'w-full bg-[#0a0908] border border-white/10 rounded-2xl px-4 py-3.5 text-[#f5f2ee] placeholder-[#71716b] text-sm outline-none transition-colors duration-300 focus:border-[#f15a24]/60';

const RestaurantModal = ({ open, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [service, setService] = useState('delivery');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/restaurants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurant_name: name, email, city, service }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Something went wrong.');
      setDone(true);
      toast.success(
        data.already_registered
          ? 'This email is already registered.'
          : 'Restaurant registered — welcome aboard.'
      );
    } catch (err) {
      toast.error(err.message || 'Could not register right now.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-md bg-[#121110] border border-white/10 rounded-t-3xl sm:rounded-3xl p-7 sm:p-8 max-h-[92vh] overflow-y-auto"
            data-testid="restaurant-modal"
          >
            <button
              onClick={onClose}
              aria-label="Close"
              data-testid="restaurant-modal-close-btn"
              className="absolute top-5 right-5 text-[#a8a29e] hover:text-[#f5f2ee] transition-colors duration-200"
            >
              <X size={20} />
            </button>

            {!done ? (
              <>
                <h3 className="font-display font-medium text-2xl sm:text-3xl text-[#f5f2ee]">
                  Register your restaurant
                </h3>
                <p className="mt-3 text-sm text-[#a8a29e] leading-relaxed">
                  Put your kitchen online with a page of your own — the people who
                  already love you can order direct.
                </p>

                <div className="mt-6 bg-[#0a0908] border border-white/10 rounded-2xl divide-y divide-white/5">
                  {PERKS.map(({ Icon, label }) => (
                    <div key={label} className="flex items-center gap-3 px-4 py-3.5">
                      <span className="w-8 h-8 rounded-xl bg-[#f15a24]/15 flex items-center justify-center shrink-0">
                        <Icon size={15} className="text-[#f15a24]" aria-hidden="true" />
                      </span>
                      <span className="text-sm text-[#f5f2ee]">{label}</span>
                    </div>
                  ))}
                </div>

                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="restaurant-signup-form">
                  <div>
                    <label htmlFor="r-name" className="block text-xs font-medium text-[#a8a29e] mb-1.5">Restaurant name</label>
                    <input
                      id="r-name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Nonna's Kitchen"
                      data-testid="restaurant-name-input"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="r-email" className="block text-xs font-medium text-[#a8a29e] mb-1.5">Email</label>
                    <input
                      id="r-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="owner@restaurant.com"
                      data-testid="restaurant-email-input"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="r-city" className="block text-xs font-medium text-[#a8a29e] mb-1.5">City / area (optional)</label>
                    <input
                      id="r-city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="e.g. Toronto"
                      data-testid="restaurant-city-input"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[#a8a29e] mb-1.5">How do you serve customers?</p>
                    <div className="grid grid-cols-3 gap-2">
                      {SERVICES.map(({ id, label, Icon }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setService(id)}
                          data-testid={`restaurant-service-${id}`}
                          aria-pressed={service === id}
                          className={`flex items-center justify-center gap-1.5 rounded-full py-2.5 text-xs font-semibold transition-colors duration-300 ${
                            service === id
                              ? 'bg-[#f15a24] text-white'
                              : 'bg-[#0a0908] border border-white/10 text-[#a8a29e] hover:border-white/25'
                          }`}
                        >
                          <Icon size={13} aria-hidden="true" /> {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="restaurant-submit-btn"
                    className="w-full rounded-full bg-[#f15a24] hover:bg-[#ff6a35] text-white font-semibold text-sm py-4 transition-colors duration-300 disabled:opacity-60"
                  >
                    {loading ? 'Registering…' : 'Register my restaurant'}
                  </motion.button>
                </form>
              </>
            ) : (
              <div className="text-center py-6" data-testid="restaurant-success">
                <span className="mx-auto w-14 h-14 rounded-full bg-[#f15a24]/15 flex items-center justify-center">
                  <Store size={22} className="text-[#f15a24]" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-display font-medium text-2xl text-[#f5f2ee]">Kitchen's on the list</h3>
                <p className="mt-3 text-sm text-[#a8a29e] leading-relaxed">
                  We'll reach out the moment partner onboarding opens in your area.
                </p>
              </div>
            )}

            <p className="mt-5 text-center text-xs text-[#71716b]">
              Prefer the web?{' '}
              <a
                href="https://dineora.ca/join"
                target="_blank"
                rel="noreferrer"
                data-testid="restaurant-dineora-join-link"
                className="text-[#f15a24] hover:text-[#ff6a35] underline underline-offset-4 transition-colors duration-200"
              >
                Register at dineora.ca/join
              </a>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const RestaurantSection = () => {
  const [open, setOpen] = useState(false);

  return (
    <section id="restaurants" className="relative bg-[#111110] py-20 sm:py-24 px-6 sm:px-10" data-testid="restaurant-signup-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono2 text-[11px] uppercase tracking-[0.45em] text-[#f15a24]">
            For restaurants
          </p>
          <h2 className="mt-4 font-display font-medium text-3xl sm:text-4xl text-[#f5f2ee] leading-[1.15]">
            Put your kitchen on the map.
          </h2>
          <p className="mt-5 text-[#a8a29e] text-base sm:text-lg leading-relaxed max-w-lg">
            DineOra gives independent restaurants a page of their own — your menu,
            your prices, your regulars. No one standing in between.
          </p>
          <div className="mt-8 space-y-3 max-w-md">
            {PERKS.map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-3 bg-[#141210] border border-white/10 rounded-2xl px-4 py-3.5">
                <span className="w-8 h-8 rounded-xl bg-[#f15a24]/15 flex items-center justify-center shrink-0">
                  <Icon size={15} className="text-[#f15a24]" aria-hidden="true" />
                </span>
                <span className="text-sm text-[#f5f2ee]">{label}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <motion.button
              onClick={() => setOpen(true)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              data-testid="restaurant-open-modal-btn"
              className="rounded-full bg-[#f15a24] hover:bg-[#ff6a35] text-white font-semibold text-sm px-8 py-4 transition-colors duration-300"
            >
              Register your restaurant
            </motion.button>
            <a
              href="https://dineora.ca/join"
              target="_blank"
              rel="noreferrer"
              data-testid="restaurant-join-external-link"
              className="inline-flex items-center gap-1.5 text-sm text-[#a8a29e] hover:text-[#f15a24] transition-colors duration-300"
            >
              dineora.ca/join <ArrowUpRight size={14} aria-hidden="true" />
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 48, rotate: 1.5 }}
          whileInView={{ opacity: 1, y: 0, rotate: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="absolute -inset-3 rounded-[2rem] border border-[#f15a24]/25 rotate-2" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-[2rem] aspect-[4/5] max-h-[560px] w-full">
            <img
              src="https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxhcnRpc2FuYWwlMjByZXN0YXVyYW50JTIwZm9vZCUyMHBsYXRlZCUyMGdvdXJtZXQlMjBjYW5hZGlhbiUyMGRpbmluZ3xlbnwwfHx8fDE3ODk1MjAxNDR8MA&ixlib=rb-4.1.0&q=85"
              alt="Chef plating a signature dish in an independent kitchen"
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908]/70 via-transparent to-transparent" aria-hidden="true" />
            <p className="absolute bottom-6 left-6 right-6 font-display italic text-lg text-[#f5f2ee]/90">
              "Your regulars, ordering direct."
            </p>
          </div>
        </motion.div>
      </div>

      <RestaurantModal open={open} onClose={() => setOpen(false)} />
    </section>
  );
};

export default RestaurantSection;
